import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Stack,
  TextField,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  Callout,
  DirectionalHint,
  Toggle,
  Label,
  MessageBar,
  MessageBarType,
  Spinner,
  IconButton,
  Text,
  Pivot,
  PivotItem,
  Dialog,
  DialogType,
  DialogFooter,
} from '@fluentui/react';
import axios from 'axios';
import { IBestellposition, IArtikel, IKreditor, IMengeneinheit, GenehmigungsStatus, BestellStatus, IUserPermissions } from '../../shared/models';
import { bestellungenApi, artikelApi, kreditorenApi, mengeneinheitenApi, lagerorteApi } from '../services/api';
import { FreigabeFlowZuordnung } from './FreigabeFlowZuordnung';
import { PositionKommentare } from './PositionKommentare';
import { ArtikelAnlegenDialog } from './ArtikelAnlegenDialog';
import { KreditorAnlegenDialog } from './KreditorAnlegenDialog';
import { centeredModalStylesWide, modalContentStyles } from '../styles/dialogs';
import { useAuth } from '../context/AuthContext';
import { TabBadge } from './TabBadge';

interface IBestellpositionFormularProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: () => void;
  bestellungId: number;
  position?: IBestellposition;
  permissions?: IUserPermissions | null;
  isOwner?: boolean;
  viewOnly?: boolean;
  defaultValues?: Partial<IBestellposition>;
}

// Mengeneinheiten werden dynamisch aus der Datenbank geladen

export const BestellpositionFormular: React.FC<IBestellpositionFormularProps> = ({
  isOpen,
  onDismiss,
  onSave,
  bestellungId,
  position,
  permissions,
  isOwner,
  viewOnly = false,
  defaultValues,
}) => {
  const { user } = useAuth();
  const [positionPermissions, setPositionPermissions] = useState<IUserPermissions | null>(permissions || null);

  // Lade positionsspezifische Berechtigungen wenn Position existiert
  useEffect(() => {
    const loadPositionPermissions = async () => {
      if (isOpen && user?.email && bestellungId) {
        try {
          const response = await axios.get('/api/permissions/me/resource', {
            params: {
              email: user.email,
              bestellungId: bestellungId,
              positionId: position?.PosID // Lädt bestellungweite Permissions wenn undefined
            }
          });
          setPositionPermissions(response.data);
        } catch (error) {
          console.error('[BestellpositionFormular] Fehler beim Laden der Berechtigungen:', error);
          setPositionPermissions(permissions || null); // Fallback auf übergebene permissions
        }
      } else {
        setPositionPermissions(permissions || null);
      }
    };

    loadPositionPermissions();
  }, [isOpen, user?.email, bestellungId, position?.PosID, permissions]);

  // Prüfe ob Benutzer die Position bearbeiten darf
  // Admin/Einkauf können alles bearbeiten
  // Ersteller können ihre eigenen Positionen bearbeiten
  // Benutzer mit temporärer Berechtigung können die spezifische Bestellung/Position bearbeiten
  const canEdit = !viewOnly && !!(
    positionPermissions?.isAdmin ||
    positionPermissions?.isEinkauf ||
    (positionPermissions?.canEdit && isOwner) ||
    positionPermissions?.hasTemporaryPermission
  );

  // Nur Admin und Einkauf dürfen den Bestellstatus manuell ändern
  const canEditStatus = !viewOnly && !!(
    positionPermissions?.isAdmin ||
    positionPermissions?.isEinkauf
  );

  // Admin/Einkauf/GF dürfen Positionen direkt freigeben
  const canDirectApprove = !viewOnly && !!(
    positionPermissions?.isAdmin ||
    positionPermissions?.isEinkauf ||
    positionPermissions?.isGeschaeftsfuehrer
  );

  const [autoApprove, setAutoApprove] = useState(false);

  // autoApprove zurücksetzen wenn Dialog geöffnet/geschlossen wird
  useEffect(() => {
    if (!isOpen) setAutoApprove(false);
  }, [isOpen]);

  const [formData, setFormData] = useState<Partial<IBestellposition>>({
    Artikelname: '',
    Artikelnummer: '',
    BCNummer: '',
    BCBestellnummer: '',
    Artikelbeschreibung: '',
    Artikelhyperlink: '',
    Menge: 1,
    Mengeneinheit: 'Stk',
    Einzelpreis: 0,
    PreisGesamt: 0,
    KreditorString: '',
    EmpfangendeKostenstelle: '',
    Sachkonto: '',
    InventarAnlagennummer: '',
    LieferterminKW: '',
    LagerortCode: 'BÖGRA',
    KostenstelleInBCUebertragen: false,
    Genehmigungsstatus: GenehmigungsStatus.Offen,
    Bestellstatus: BestellStatus.Eingereicht,
  });

  const [artikel, setArtikel] = useState<IArtikel[]>([]);
  const [kreditoren, setKreditoren] = useState<IKreditor[]>([]);
  const [mengeneinheiten, setMengeneinheiten] = useState<IMengeneinheit[]>([]);
  const [lagerorte, setLagerorte] = useState<{ Code: string; Name: string | null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Dialog: Preis nach BC-Anlegen geändert
  const [bcPreisDialog, setBcPreisDialog] = useState<{
    open: boolean;
    loading: boolean;
    error?: string;
    bcBestellnummer: string;
    positionId: number;
  } | null>(null);

  // Dialog States
  const [showArtikelDialog, setShowArtikelDialog] = useState(false);
  const [showKreditorDialog, setShowKreditorDialog] = useState(false);
  const [positionBadges, setPositionBadges] = useState<{
    kommentare: { total: number; unread: number; hasMention: boolean };
  } | null>(null);

  // Such-States für Artikel und Kreditor (TextField + Callout)
  const [artikelSearchText, setArtikelSearchText] = useState('');
  const [showArtikelDropdown, setShowArtikelDropdown] = useState(false);
  const artikelContainerRef = useRef<HTMLDivElement>(null);
  const [kreditorSearchText, setKreditorSearchText] = useState('');
  const [showKreditorDropdown, setShowKreditorDropdown] = useState(false);
  const [includeBCKreditoren, setIncludeBCKreditoren] = useState(true);
  const kreditorContainerRef = useRef<HTMLDivElement>(null);
  const [includeBCArtikel, setIncludeBCArtikel] = useState(true);

  const loadPositionBadges = async (posId: number) => {
    try {
      const res = await axios.get(`/api/read-status/position/${posId}`);
      setPositionBadges(res.data);
    } catch {
      // ignorieren
    }
  };

  const markPositionTabAsRead = async (posId: number) => {
    try {
      await axios.post('/api/read-status/mark', { entityType: 'PositionKommentare', entityId: posId });
      loadPositionBadges(posId);
    } catch {
      // ignorieren
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      if (position) {
        // Ensure all required fields are populated from position
        setFormData({
          ...position,
          Artikelname: position.Artikelname || '',
          Artikelnummer: position.Artikelnummer || '',
          BCNummer: position.BCNummer || '',
          Artikelbeschreibung: position.Artikelbeschreibung || '',
          Artikelhyperlink: position.Artikelhyperlink || '',
          Menge: position.Menge || 1,
          Mengeneinheit: position.Mengeneinheit || 'Stk',
          Einzelpreis: position.Einzelpreis || 0,
          PreisGesamt: position.PreisGesamt || 0,
          KreditorString: position.KreditorString || '',
          EmpfangendeKostenstelle: position.EmpfangendeKostenstelle || '',
          Sachkonto: position.Sachkonto || '',
          InventarAnlagennummer: position.InventarAnlagennummer || '',
          LieferterminKW: position.LieferterminKW || '',
          LagerortCode: position.LagerortCode || 'BÖGRA',
          KostenstelleInBCUebertragen: !!position.KostenstelleInBCUebertragen,
          Genehmigungsstatus: position.Genehmigungsstatus || GenehmigungsStatus.Offen,
          Bestellstatus: position.Bestellstatus || BestellStatus.Eingereicht,
        });
        // Aktualisiere Input-States für flüssige Eingabe
        setMengeInput(formatDecimal(position.Menge || 1));
        setEinzelpreisInput(formatDecimal(position.Einzelpreis || 0));
        // Badge für Kommentare-Tab laden
        if (position.Id) loadPositionBadges(position.Id);
      } else {
        resetForm();
        setPositionBadges(null);
      }
    }
  }, [isOpen, position]);

  useEffect(() => {
    // Berechne Gesamtpreis
    const menge = formData.Menge || 0;
    const einzelpreis = formData.Einzelpreis || 0;
    setFormData((prev) => ({
      ...prev,
      PreisGesamt: menge * einzelpreis,
    }));
  }, [formData.Menge, formData.Einzelpreis]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [artikelData, kreditorenData, mengeneinheitenData, lagerorteData] = await Promise.all([
        artikelApi.getAll(),
        kreditorenApi.getAll(),
        mengeneinheitenApi.getAll(),
        lagerorteApi.getAll(),
      ]);
      setArtikel(artikelData);
      setKreditoren(kreditorenData);
      setMengeneinheiten(mengeneinheitenData);
      setLagerorte(lagerorteData);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Hilfsfunktion: Default-Wert nur übernehmen wenn er nicht 's. Position' oder leer ist
  const getDefault = (value?: string): string => {
    if (!value || value === 's. Position') return '';
    return value;
  };

  const resetForm = () => {
    setFormData({
      Artikelname: '',
      Artikelnummer: '',
      BCNummer: '',
      BCBestellnummer: '',
      Artikelbeschreibung: '',
      Artikelhyperlink: '',
      Menge: 1,
      Mengeneinheit: 'Stk',
      Einzelpreis: 0,
      PreisGesamt: 0,
      KreditorString: getDefault(defaultValues?.KreditorString),
      EmpfangendeKostenstelle: getDefault(defaultValues?.EmpfangendeKostenstelle),
      Sachkonto: getDefault(defaultValues?.Sachkonto),
      InventarAnlagennummer: getDefault(defaultValues?.InventarAnlagennummer),
      LieferterminKW: getDefault(defaultValues?.LieferterminKW),
      LagerortCode: getDefault(defaultValues?.LagerortCode) || 'BÖGRA',
      KostenstelleInBCUebertragen: false,
      Genehmigungsstatus: (getDefault(defaultValues?.Genehmigungsstatus) as any) || GenehmigungsStatus.Offen,
      Bestellstatus: (Object.values(BestellStatus).includes(defaultValues?.Bestellstatus as BestellStatus)
        ? defaultValues?.Bestellstatus as BestellStatus
        : BestellStatus.Eingereicht),
    });
    setMengeInput('1');
    setEinzelpreisInput('0');
  };

  const handleArtikelCreated = (newArtikel: IArtikel) => {
    // Füge neuen Artikel zur Liste hinzu
    setArtikel((prev) => [...prev, newArtikel]);
    // Setze den neuen Artikel als ausgewählt
    setFormData((prev) => ({
      ...prev,
      Artikelname: newArtikel.Artikelname || '',
      Artikelnummer: newArtikel.Artikelnummer || '',
      BCNummer: newArtikel.BCNummer || '',
      Artikelbeschreibung: newArtikel.Artikelbeschreibung || '',
      Artikelhyperlink: newArtikel.Artikelhyperlink || '',
      Einzelpreis: newArtikel.Einzelpreis || 0,
    }));
    setEinzelpreisInput(formatDecimal(newArtikel.Einzelpreis || 0));
  };

  const handleKreditorCreated = (newKreditor: IKreditor) => {
    // Füge neuen Kreditor zur Liste hinzu
    setKreditoren((prev) => [...prev, newKreditor]);
    // Setze den neuen Kreditor als ausgewählt
    setFormData((prev) => ({
      ...prev,
      KreditorString: newKreditor.Kreditorname || '',
    }));
  };

  // Dropdown-Options für Mengeneinheiten (dynamisch aus Datenbank)
  const mengeneinheitenOptions: IDropdownOption[] = useMemo(() => {
    return mengeneinheiten.map((me) => ({
      key: me.Kurzform || me.Einheit,
      text: `${me.Einheit} (${me.Kurzform})`,
    }));
  }, [mengeneinheiten]);

  // Dropdown-Options für Lagerorte (dynamisch aus Datenbank)
  const lagerortOptions: IDropdownOption[] = useMemo(() => {
    const opts = lagerorte.map((l) => ({
      key: l.Code,
      text: l.Name ? `${l.Code} – ${l.Name}` : l.Code,
    }));
    // Sicherstellen dass BÖGRA immer vorhanden ist
    if (!opts.find(o => o.key === 'BÖGRA')) {
      opts.unshift({ key: 'BÖGRA', text: 'BÖGRA – Bögra Hauptlager' });
    }
    return opts;
  }, [lagerorte]);

  // String-Werte für flüssige Eingabe von Zahlen mit Komma
  const [mengeInput, setMengeInput] = useState<string>('1');
  const [einzelpreisInput, setEinzelpreisInput] = useState<string>('0');

  // Hilfsfunktion zum Parsen von Dezimalzahlen (Komma oder Punkt)
  const parseDecimal = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Formatiere Zahl für Anzeige (mit Komma)
  const formatDecimal = (value: number | undefined): string => {
    if (value === undefined || value === null) return '';
    return value.toString().replace('.', ',');
  };

  const handleSave = async () => {
    // Validierung
    if (!formData.Artikelname) {
      setError('Artikelname ist erforderlich');
      return;
    }
    if (!formData.Menge || formData.Menge <= 0) {
      setError('Menge muss größer als 0 sein');
      return;
    }
    if (!formData.KreditorString) {
      setError('Kreditor ist erforderlich');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (position?.Id) {
        // Update existing position
        await bestellungenApi.updatePosition(bestellungId, position.Id, formData);

        // Preis in BC-Bestellung geändert? Dialog anzeigen.
        const hatBCBestellung = !!position.BCBestellnummer;
        const preisGeaendert = Number(formData.Einzelpreis) !== Number(position.Einzelpreis);
        if (hatBCBestellung && preisGeaendert) {
          onSave(); // Liste im Hintergrund aktualisieren
          setBcPreisDialog({
            open: true,
            loading: false,
            bcBestellnummer: position.BCBestellnummer!,
            positionId: position.Id,
          });
          return; // Formular noch offen lassen bis Nutzer Dialog beantwortet
        }
      } else {
        // Create new position
        await bestellungenApi.createPosition(bestellungId, formData);
      }

      onSave();
      onDismiss();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern der Position');
    } finally {
      setSaving(false);
    }
  };

  // Filtere Artikel basierend auf Toggle und Suchtext
  const getArtikelSuggestions = (): IArtikel[] => {
    const base = includeBCArtikel
      ? artikel
      : artikel.filter((a) => !a.BCNummer);
    if (!artikelSearchText) return base;
    const searchLower = artikelSearchText.toLowerCase();
    return base.filter(
      (a) =>
        a.Artikelname?.toLowerCase().includes(searchLower) ||
        a.Artikelnummer?.toLowerCase().includes(searchLower) ||
        a.BCNummer?.toLowerCase().includes(searchLower)
    );
  };

  // Filtere Kreditoren basierend auf Toggle und Suchtext
  const getKreditorSuggestions = (): IKreditor[] => {
    const base = includeBCKreditoren
      ? kreditoren
      : kreditoren.filter((k) => !k.Kreditorennummer);
    if (!kreditorSearchText) return base;
    const searchLower = kreditorSearchText.toLowerCase();
    return base.filter(
      (k) =>
        k.Kreditorname?.toLowerCase().includes(searchLower) ||
        k.Kreditorennummer?.toLowerCase().includes(searchLower)
    );
  };

  const headerText = viewOnly
    ? 'Bestellposition ansehen'
    : position ? 'Bestellposition bearbeiten' : 'Neue Bestellposition';

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={true}
      styles={centeredModalStylesWide}
    >
      {/* Header */}
      <div style={modalContentStyles.header}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          {headerText}
        </Text>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Schließen"
          onClick={onDismiss}
          disabled={saving}
        />
      </div>

      {/* Body */}
      <div style={modalContentStyles.body}>
        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)} styles={{ root: { marginBottom: 8 } }}>
            {error}
          </MessageBar>
        )}
        {!canEdit && (
          <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 8 } }}>
            Sie haben keine Berechtigung, diese Position zu bearbeiten. Sie können nur die Informationen ansehen und Kommentare hinzufügen.
          </MessageBar>
        )}

        <Pivot
          styles={{ root: { marginBottom: 8 } }}
          onLinkClick={(item) => {
            if (item?.props.itemKey === 'pos-kommentare' && formData.Id) {
              markPositionTabAsRead(formData.Id);
            }
          }}
        >

          {/* ── Tab 1: Position ── */}
          <PivotItem headerText="Position" itemIcon="EditNote">
      <Stack tokens={{ childrenGap: 16, padding: '16px 0 0 0' }}>
        {loading ? (
          <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
            <Spinner label="Lade Daten..." />
          </Stack>
        ) : (
          <>
            {/* Zeile 1: Info-Felder (readonly) */}
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <TextField
                label="Best.-ID / Pos-ID"
                value={`${bestellungId || ''} / ${formData.PosID || 'Auto'}`}
                disabled
                styles={{ root: { width: '33%' } }}
              />
              <TextField
                label="BC-Bestellnummer"
                placeholder="z.B. BEW-0001"
                value={formData.BCBestellnummer || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, BCBestellnummer: value }))
                }
                disabled={!canEditStatus}
                description="Bestellnummer in Business Central"
                styles={{ root: { width: '33%' } }}
              />
              <TextField
                label="Gesamtsumme"
                value={new Intl.NumberFormat('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(formData.PreisGesamt || 0)}
                disabled
                styles={{ root: { width: '33%' } }}
              />
            </Stack>

            {/* Zeile 2: Status-Felder */}
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <TextField
                label="Genehmigungsstatus"
                value={formData.Genehmigungsstatus || 'Offen'}
                disabled
                styles={{ root: { width: '33%' } }}
              />
              <Dropdown
                label="Bestellstatus"
                selectedKey={formData.Bestellstatus || BestellStatus.Eingereicht}
                options={[
                  { key: BestellStatus.Eingereicht, text: BestellStatus.Eingereicht },
                  { key: BestellStatus.Bestellt, text: BestellStatus.Bestellt },
                  { key: BestellStatus.TeilweiseGeliefert, text: BestellStatus.TeilweiseGeliefert },
                  { key: BestellStatus.Geliefert, text: BestellStatus.Geliefert },
                ]}
                onChange={(_, option) =>
                  setFormData((prev) => ({ ...prev, Bestellstatus: option?.key as BestellStatus }))
                }
                disabled={!canEditStatus}
                styles={{ root: { width: '33%' } }}
              />
              {/* Unsichtbarer Dummy für Spaltenausrichtung */}
              <div style={{ width: '33%' }} />
            </Stack>

            {/* Zeile 3: Liefertermin + Lagerortcode */}
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <TextField
                label="gewünschter Liefertermin (KW)"
                placeholder="z.B. 15 oder 15-2025"
                value={formData.LieferterminKW || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, LieferterminKW: value }))
                }
                disabled={!canEdit}
                styles={{ root: { width: '33%' } }}
              />
              <Dropdown
                label="Lagerort-Code"
                selectedKey={formData.LagerortCode || 'BÖGRA'}
                options={lagerortOptions}
                onChange={(_, option) =>
                  setFormData((prev) => ({ ...prev, LagerortCode: option?.key as string }))
                }
                disabled={!canEdit}
                styles={{ root: { width: '33%' } }}
              />
              {/* Unsichtbarer Dummy für Spaltenausrichtung */}
              <div style={{ width: '33%' }} />
            </Stack>

            {/* Zeile 4: Kostenstelle, Sachkonto, Inventar */}
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <Stack styles={{ root: { width: '33%' } }} tokens={{ childrenGap: 4 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                  <Label>Empfangende Kostenstelle</Label>
                  {(positionPermissions?.isAdmin || positionPermissions?.isEinkauf) && (
                    <Toggle
                      label="In BC übertragen"
                      inlineLabel
                      checked={!!formData.KostenstelleInBCUebertragen}
                      onChange={(_, checked) =>
                        setFormData((prev) => ({ ...prev, KostenstelleInBCUebertragen: !!checked }))
                      }
                      disabled={!canEdit}
                      styles={{ root: { marginBottom: 0 } }}
                    />
                  )}
                </Stack>
                <TextField
                  placeholder="z.B. 9430"
                  value={formData.EmpfangendeKostenstelle || ''}
                  onChange={(_, value) =>
                    setFormData((prev) => ({ ...prev, EmpfangendeKostenstelle: value }))
                  }
                  disabled={!canEdit}
                />
              </Stack>
              <TextField
                label="Sachkonto"
                value={formData.Sachkonto || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, Sachkonto: value }))
                }
                disabled={!canEdit}
                styles={{ root: { width: '33%' } }}
              />
              <TextField
                label="Inventar-/Anlagennummer"
                value={formData.InventarAnlagennummer || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, InventarAnlagennummer: value }))
                }
                disabled={!canEdit}
                styles={{ root: { width: '33%' } }}
              />
            </Stack>

            {/* Zeile 4: Kreditor mit Toggle und Suchfeld */}
            <Stack tokens={{ childrenGap: 6 }}>
              <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                <Label required>Kreditor</Label>
                <Toggle
                  label="inkl. BC"
                  inlineLabel
                  checked={includeBCKreditoren}
                  onChange={(_, checked) => setIncludeBCKreditoren(!!checked)}
                  disabled={!canEdit}
                  styles={{ root: { marginBottom: 0 } }}
                />
              </Stack>
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                <div ref={kreditorContainerRef} style={{ flexGrow: 1, position: 'relative' }}>
                  <TextField
                    placeholder="Kreditor suchen (Name oder Nummer)..."
                    value={kreditorSearchText || formData.KreditorString || ''}
                    onFocus={() => {
                      if (kreditorSearchText) setShowKreditorDropdown(true);
                    }}
                    onChange={(_, value) => {
                      setKreditorSearchText(value || '');
                      setShowKreditorDropdown(!!(value && value.length > 0));
                    }}
                    disabled={!canEdit}
                    errorMessage={!formData.KreditorString ? 'Kreditor ist erforderlich' : ''}
                    styles={{ root: { width: '100%' } }}
                  />
                  {showKreditorDropdown && kreditorSearchText && (
                    <Callout
                      target={kreditorContainerRef}
                      isBeakVisible={false}
                      directionalHint={DirectionalHint.bottomLeftEdge}
                      onDismiss={() => setShowKreditorDropdown(false)}
                      calloutMaxWidth={kreditorContainerRef.current?.offsetWidth || 400}
                      styles={{ calloutMain: { minWidth: kreditorContainerRef.current?.offsetWidth || 300, maxHeight: 300, overflowY: 'auto' } }}
                    >
                      <Stack>
                        {getKreditorSuggestions().length === 0 ? (
                          <Stack tokens={{ padding: '8px 12px' }}>
                            <Text styles={{ root: { color: '#605e5c' } }}>Keine Kreditoren gefunden</Text>
                          </Stack>
                        ) : (
                          getKreditorSuggestions().map((k) => (
                            <Stack
                              key={k.Id}
                              horizontal
                              tokens={{ padding: '7px 12px', childrenGap: 8 }}
                              styles={{
                                root: {
                                  cursor: 'pointer',
                                  selectors: { ':hover': { backgroundColor: '#f3f2f1' } },
                                },
                              }}
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, KreditorString: k.Kreditorname || '' }));
                                setKreditorSearchText('');
                                setShowKreditorDropdown(false);
                              }}
                            >
                              <Text>{k.Kreditorname}</Text>
                              {k.Kreditorennummer && (
                                <Text styles={{ root: { color: '#605e5c' } }}>
                                  ({k.Kreditorennummer})
                                </Text>
                              )}
                            </Stack>
                          ))
                        )}
                      </Stack>
                    </Callout>
                  )}
                </div>
                <PrimaryButton
                  text="Neu..."
                  iconProps={{ iconName: 'Add' }}
                  onClick={() => setShowKreditorDialog(true)}
                  disabled={!canEdit}
                  styles={{ root: { marginBottom: '4px' } }}
                />
              </Stack>
            </Stack>

            {/* Zeile 5: Artikelname mit Suchfeld */}
            <Stack tokens={{ childrenGap: 6 }}>
              <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                <Label required>Artikelname</Label>
                <Toggle
                  label="inkl. BC"
                  inlineLabel
                  checked={includeBCArtikel}
                  onChange={(_, checked) => setIncludeBCArtikel(!!checked)}
                  disabled={!canEdit}
                  styles={{ root: { marginBottom: 0 } }}
                />
              </Stack>
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                <div ref={artikelContainerRef} style={{ flexGrow: 1, position: 'relative' }}>
                  <TextField
                    placeholder="Artikel suchen (Name, Nummer oder Bögra-Nr.)..."
                    value={artikelSearchText || formData.Artikelname || ''}
                    onFocus={() => {
                      if (artikelSearchText) setShowArtikelDropdown(true);
                    }}
                    onChange={(_, value) => {
                      setArtikelSearchText(value || '');
                      setFormData((prev) => ({ ...prev, Artikelname: value || '' }));
                      setShowArtikelDropdown(!!(value && value.length > 0));
                    }}
                    disabled={!canEdit}
                    errorMessage={!formData.Artikelname ? 'Artikelname ist erforderlich' : ''}
                    styles={{ root: { width: '100%' } }}
                  />
                  {showArtikelDropdown && artikelSearchText && (
                    <Callout
                      target={artikelContainerRef}
                      isBeakVisible={false}
                      directionalHint={DirectionalHint.bottomLeftEdge}
                      onDismiss={() => setShowArtikelDropdown(false)}
                      calloutMaxWidth={artikelContainerRef.current?.offsetWidth || 400}
                      styles={{ calloutMain: { minWidth: artikelContainerRef.current?.offsetWidth || 300, maxHeight: 300, overflowY: 'auto' } }}
                    >
                      <Stack>
                        {getArtikelSuggestions().length === 0 ? (
                          <Stack tokens={{ padding: '8px 12px' }}>
                            <Text styles={{ root: { color: '#605e5c' } }}>Keine Artikel gefunden</Text>
                          </Stack>
                        ) : (
                          getArtikelSuggestions().map((a) => (
                            <Stack
                              key={a.Id}
                              tokens={{ padding: '7px 12px' }}
                              styles={{
                                root: {
                                  cursor: 'pointer',
                                  selectors: { ':hover': { backgroundColor: '#f3f2f1' } },
                                },
                              }}
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  Artikelname: a.Artikelname || '',
                                  Artikelnummer: a.Artikelnummer || '',
                                  BCNummer: a.BCNummer || '',
                                  Artikelbeschreibung: a.Artikelbeschreibung || '',
                                  Artikelhyperlink: a.Artikelhyperlink || '',
                                  Einzelpreis: a.Einzelpreis || 0,
                                  Mengeneinheit: a.Mengeneinheit || 'Stk',
                                }));
                                setEinzelpreisInput(formatDecimal(a.Einzelpreis || 0));
                                setArtikelSearchText('');
                                setShowArtikelDropdown(false);
                              }}
                            >
                              <Text>{a.Artikelname}</Text>
                              <Stack horizontal tokens={{ childrenGap: 8 }}>
                                {a.Artikelnummer && (
                                  <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                    Art.Nr: {a.Artikelnummer}
                                  </Text>
                                )}
                                {a.BCNummer && (
                                  <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                    Bögra-Nr: {a.BCNummer}
                                  </Text>
                                )}
                              </Stack>
                            </Stack>
                          ))
                        )}
                      </Stack>
                    </Callout>
                  )}
                </div>
                <PrimaryButton
                  text="Neu..."
                  iconProps={{ iconName: 'Add' }}
                  onClick={() => setShowArtikelDialog(true)}
                  disabled={!canEdit}
                  styles={{ root: { marginBottom: '4px' } }}
                />
              </Stack>
            </Stack>

            {/* Zeile 6: Artikelnummer und Bögra-Nr. */}
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <TextField
                label="Artikelnummer beim Lieferanten"
                placeholder="Art.Nr"
                value={formData.Artikelnummer || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, Artikelnummer: value }))
                }
                disabled={!canEdit}
                styles={{ root: { width: '50%' } }}
              />
              <TextField
                label="Bögra-Nr."
                placeholder="BC-Nr"
                value={formData.BCNummer || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, BCNummer: value }))
                }
                disabled={!canEdit}
                styles={{ root: { width: '50%' } }}
              />
            </Stack>

            {/* Offene Menge (lt. BC) – nur wenn gesetzt */}
            {formData.OffeneMenge !== undefined && formData.OffeneMenge !== null && (
              <Stack horizontal tokens={{ childrenGap: 16 }}>
                <TextField
                  label="Offene Menge (lt. BC)"
                  value={String(formData.OffeneMenge)}
                  disabled
                  description="Noch ausstehende Menge laut Business Central"
                  styles={{ root: { width: '33%' } }}
                />
              </Stack>
            )}

            {/* Zeile 7: Beschreibung */}
            <TextField
              label="Artikelbeschreibung"
              placeholder="Detaillierte Beschreibung des Artikels"
              value={formData.Artikelbeschreibung || ''}
              onChange={(_, value) =>
                setFormData((prev) => ({ ...prev, Artikelbeschreibung: value }))
              }
              disabled={!canEdit}
              multiline
              rows={4}
            />

            {/* Zeile 8: Link */}
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
              {formData.Artikelhyperlink && (
                <IconButton
                  iconProps={{ iconName: 'Link' }}
                  title="Link öffnen"
                  onClick={() => window.open(formData.Artikelhyperlink, '_blank')}
                  styles={{ root: { marginBottom: '4px' } }}
                />
              )}
              <TextField
                label="Link zum Artikel"
                placeholder="https://..."
                value={formData.Artikelhyperlink || ''}
                onChange={(_, value) =>
                  setFormData((prev) => ({ ...prev, Artikelhyperlink: value }))
                }
                disabled={!canEdit}
                styles={{ root: { flexGrow: 1 } }}
              />
            </Stack>

            {/* Zeile 9: Menge, Einheit, Preis */}
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <TextField
                label="Menge"
                type="text"
                placeholder="z.B. 5 oder 5,5"
                value={mengeInput}
                onChange={(_, value) => {
                  setMengeInput(value || '');
                  const menge = parseDecimal(value || '0');
                  setFormData((prev) => ({ ...prev, Menge: menge }));
                }}
                disabled={!canEdit}
                required
                errorMessage={
                  !formData.Menge || formData.Menge <= 0 ? 'Menge muss größer als 0 sein' : ''
                }
                styles={{ root: { width: '33%' } }}
              />
              <Dropdown
                label="Mengeneinheit"
                selectedKey={formData.Mengeneinheit || 'Stk'}
                onChange={(_, option) =>
                  setFormData((prev) => ({ ...prev, Mengeneinheit: option?.key as string }))
                }
                disabled={!canEdit}
                options={mengeneinheitenOptions}
                styles={{ root: { width: '33%' } }}
              />
              <TextField
                label="Einzelpreis (€)"
                type="text"
                placeholder="z.B. 12,50"
                value={einzelpreisInput}
                onChange={(_, value) => {
                  setEinzelpreisInput(value || '');
                  const preis = parseDecimal(value || '0');
                  setFormData((prev) => ({ ...prev, Einzelpreis: preis }));
                }}
                disabled={!canEdit}
                styles={{ root: { width: '33%' } }}
              />
            </Stack>

            {/* Info-Hinweis */}
            <MessageBar messageBarType={MessageBarType.info}>
              Felder mit * sind Pflichtfelder. Der Gesamtpreis wird automatisch berechnet.
            </MessageBar>
          </>
        )}

        {saving && (
          <Stack horizontalAlign="center" tokens={{ padding: 20 }}>
            <Spinner label="Speichere Position..." />
          </Stack>
        )}
      </Stack>
          </PivotItem>

          {/* ── Tab 2: Freigabe (nur bei bestehenden Positionen) ── */}
          {position && formData.Id && (
            <PivotItem headerText="Freigabe" itemIcon="Permissions">
              <Stack tokens={{ padding: '16px 0 0 0' }}>
                <FreigabeFlowZuordnung
                  bestellungId={bestellungId}
                  positionId={formData.Id}
                  flowType="Position"
                  currentDepartment={formData.EmpfangendeKostenstelle}
                  onFlowChange={onSave}
                />
              </Stack>
            </PivotItem>
          )}

          {/* ── Tab 3: Kommentare (nur bei bestehenden Positionen) ── */}
          {position && formData.Id && (
            <PivotItem
              itemKey="pos-kommentare"
              itemIcon="Comment"
              onRenderItemLink={() => (
                <TabBadge
                  label="Kommentare"
                  total={positionBadges?.kommentare.total ?? 0}
                  unread={positionBadges?.kommentare.unread ?? 0}
                  hasMention={positionBadges?.kommentare.hasMention ?? false}
                />
              )}
            >
              <Stack tokens={{ padding: '16px 0 0 0' }}>
                <PositionKommentare
                  bestellungId={bestellungId}
                  positionId={formData.Id}
                  posID={formData.PosID || 0}
                  onKommentarChange={async () => {
                    // markPositionTabAsRead setzt LastReadAt = jetzt (nach Created des neuen Kommentars)
                    // und lädt danach die Badges neu → kein false-positive "ungelesen"
                    if (formData.Id) await markPositionTabAsRead(formData.Id);
                    onSave();
                  }}
                />
              </Stack>
            </PivotItem>
          )}

        </Pivot>
      </div>

      {/* Footer */}
      <div style={modalContentStyles.footer}>
        {canEdit ? (
          <>
            <DefaultButton text="Abbrechen" onClick={onDismiss} disabled={saving} />
            <PrimaryButton text="Speichern" onClick={handleSave} disabled={saving || loading} />
          </>
        ) : (
          <PrimaryButton text="Schließen" onClick={onDismiss} />
        )}
      </div>

      {/* Dialoge für neue Artikel/Kreditoren */}
      <ArtikelAnlegenDialog
        isOpen={showArtikelDialog}
        onDismiss={() => setShowArtikelDialog(false)}
        onSuccess={handleArtikelCreated}
      />

      <KreditorAnlegenDialog
        isOpen={showKreditorDialog}
        onDismiss={() => setShowKreditorDialog(false)}
        onSuccess={handleKreditorCreated}
      />

      {/* Dialog: Preis nach BC-Anlegen geändert */}
      {bcPreisDialog && (
        <Dialog
          hidden={!bcPreisDialog.open}
          onDismiss={() => { setBcPreisDialog(null); onDismiss(); }}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Preis in BC-Bestellung abweichend',
            subText: `Die BC-Bestellung ${bcPreisDialog.bcBestellnummer} enthält noch den alten Preis. Was soll passieren?`,
          }}
          modalProps={{ isBlocking: true }}
          minWidth={440}
        >
          {bcPreisDialog.error && (
            <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12 } }}>
              {bcPreisDialog.error}
            </MessageBar>
          )}
          {bcPreisDialog.loading && (
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { marginBottom: 12 } }}>
              <Spinner />
              <Text>Preis wird in BC aktualisiert…</Text>
            </Stack>
          )}
          <DialogFooter>
            <DefaultButton
              text="OK"
              disabled={bcPreisDialog.loading}
              onClick={() => { setBcPreisDialog(null); onDismiss(); }}
            />
            <DefaultButton
              text="In BC öffnen"
              disabled={bcPreisDialog.loading}
              onClick={() => {
                const ebNr = encodeURIComponent(bcPreisDialog.bcBestellnummer);
                const bcClientUrl = `dynamicsnav://boegra.faveo365.com:9246/NSTBOEGRA/B%C3%96GRATechnologie%20GmbH/runpage?tenant=T5257376201000231284&page=50&personalization=50&$filter=%27Purchase%20Header%27.%27No.%27%20IS%20%27%40%2A${ebNr}%2A%27&mode=Edit`;
                window.open(bcClientUrl, '_blank');
                setBcPreisDialog(null);
                onDismiss();
              }}
            />
            <PrimaryButton
              text="Preis in BC anpassen"
              disabled={bcPreisDialog.loading}
              onClick={async () => {
                setBcPreisDialog(prev => prev ? { ...prev, loading: true, error: undefined } : prev);
                try {
                  const resp = await axios.put(`/api/bc/position/${bcPreisDialog.positionId}/preis`);
                  if (resp.data?.erfolg) {
                    setBcPreisDialog(null);
                    onDismiss();
                  } else {
                    setBcPreisDialog(prev => prev ? { ...prev, loading: false, error: resp.data?.fehler || 'Unbekannter Fehler' } : prev);
                  }
                } catch (e: any) {
                  const msg = e.response?.data?.fehler || e.response?.data?.error || e.message || 'Fehler beim Aktualisieren';
                  setBcPreisDialog(prev => prev ? { ...prev, loading: false, error: msg } : prev);
                }
              }}
            />
          </DialogFooter>
        </Dialog>
      )}
    </Modal>
  );
};
